import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../store";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import HorizontalLayout from "../../app/layouts/HorizontalLayout";
import PageContent from "../../app/layouts/PageContent";
import Card from "../../components/common/Card";
import useOrganization from "../organization/hooks/useOrganization";
import { getQuestionnaire } from "./api/survey.api";
import QuestionRenderer from "./components/QuestionRenderer";
import type { Question } from "./types/question.types";
import {
  getInitialPage,
  getCurrentQuestionOrdinal,
  isQuestionAnswered,
} from "./utils/survey.utils";
import useSurvey from "./hooks/useSurvey";
import { saveSurveyAnswers } from "./store/thunk";
import { setNewSurveyStarted } from "../auth/store/login/reducer";
import SurveyComplete from "./SurveyComplete";
import ExitModal from "./ExitModal";

export default function Survey() {
  document.title = `Survey - ${process.env.REACT_APP_NAME}`;

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  const {
    surveyAnswers: answers,
    questionnaireId: surveyQuestionnaireId,
    isCompleted,
    isNewSurvey,
  } = useSurvey();

  const [questions, setQuestions] = useState<Question[] | []>([]);
  const [currentPage, setCurrentPage] = useState(0);

  const [draftAnswers, setDraftAnswers] = useState<Record<
    string,
    number
  > | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isSurveyLoading, setIsSurveyLoading] = useState(true);
  const totalPages = questions.length;

  useEffect(() => {
    if (isCompleted) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isCompleted]);

  const currentQuestion = questions[currentPage] ?? null;
  const progress =
    totalPages === 0 ? 0 : Math.round(((currentPage + 1) / totalPages) * 100);

  const displayAnswers = { ...answers, ...(draftAnswers ?? {}) };

  const setAnswer = useCallback((answerKey: string, value: number) => {
    setDraftAnswers((prev) => ({ ...prev, [answerKey]: value }));
  }, []);

  const setAnswersBatch = useCallback((updates: Record<string, number>) => {
    setDraftAnswers((prev) => ({ ...prev, ...updates }));
  }, []);

  const goNext = useCallback(async (immediateAnswers?: Record<string, number>) => {
    if (questions.length === 0 || !currentQuestion) return;

    if (currentQuestion.layout === "instructions") {
      setDraftAnswers(null);
      setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
      return;
    }

    const questionnaireId =
      surveyQuestionnaireId ?? Number(currentOrganization?.default_survey);
    if (!questionnaireId || !Number.isFinite(questionnaireId)) {
      toast.error("Survey not loaded. Please refresh and try again.");
      return;
    }
    const score = { ...answers, ...(draftAnswers ?? {}), ...(immediateAnswers ?? {}) };
    if (
      Object.keys(score).length === 0 ||
      !isQuestionAnswered(currentQuestion, score)
    ) {
      toast.error("Please answer the question before continuing.");
      return;
    }
    const completionPer = Math.round(((currentPage + 1) / totalPages) * 100);

    setIsSaving(true);
    try {
      await dispatch(
        saveSurveyAnswers({
          questionnaire_id: questionnaireId,
          completion_per: completionPer,
          score,
        }),
      ).unwrap();
      // First answer of a queued retake is now persisted — the old run is superseded,
      // so stop masking and let this run's progress drive the UI.
      if (isNewSurvey) {
        dispatch(setNewSurveyStarted());
      }
      setDraftAnswers(null);
      setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [
    dispatch,
    isNewSurvey,
    answers,
    currentQuestion,
    currentOrganization?.default_survey,
    currentPage,
    draftAnswers,
    questions.length,
    surveyQuestionnaireId,
    totalPages,
  ]);

  const handleAnswerChangeAndAdvance = useCallback(
    (answerKey: string, value: number) => {
      if (isSaving) return;
      setAnswer(answerKey, value);
      void goNext({ [answerKey]: value });
    },
    [isSaving, setAnswer, goNext],
  );

  const handleAnswersBatchAndMaybeAdvance = useCallback(
    (updates: Record<string, number>) => {
      if (isSaving) return;
      setAnswersBatch(updates);
      if (
        currentQuestion?.layout === "multiple_choice" &&
        Number(currentQuestion.attributes.allowed_choices) === 1
      ) {
        void goNext(updates);
      }
    },
    [isSaving, setAnswersBatch, currentQuestion, goNext],
  );

  const goPrevious = useCallback(() => {
    setDraftAnswers(null);
    setCurrentPage((p) => Math.max(0, p - 1));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const id = currentOrganization?.default_survey;
    const questionnaireId = Number(id);

    const load = async () => {
      setIsSurveyLoading(true);
      try {
        const response = await getQuestionnaire(questionnaireId);
        if (cancelled) return;
        if (response.success && response.data) {
          // Admin stores questions with Q1 at the end of the array (unshift + numbering); reverse for survey flow Q1 first.
          const orderedQuestions = [...response.data.questions].reverse();
          setQuestions(orderedQuestions);
          if (orderedQuestions.length) {
            setCurrentPage(getInitialPage(orderedQuestions, answers));
          }
        } else {
          toast.error("Unable to load the survey. Please try again later.");
        }
      } catch {
        if (!cancelled) {
          toast.error("Unable to load the survey. Please try again later.");
        }
      } finally {
        if (!cancelled) {
          setIsSurveyLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
    // Only run when survey id changes — not when answers change, so Save and Continue doesn't jump to first unanswered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrganization?.default_survey]);

  if (isCompleted) {
    return (
      <HorizontalLayout showNav={false}>
        <PageContent>
          <SurveyComplete />
        </PageContent>
      </HorizontalLayout>
    );
  }

  return (
    <HorizontalLayout showNav={false}>
      <PageContent>
        <Card fullHeight className="relative flex flex-col">
          <div className="flex-shrink-0 mb-6">
            <button
              type="button"
              onClick={() => navigate("/locker-room")}
              className="inline-flex items-center gap-2 text-[#26295e] font-primary font-medium text-[15px] no-underline hover:text-[#3b519c] transition-colors bg-transparent border-0 cursor-pointer p-0"
            >
              <svg
                viewBox="0 0 40 28"
                className="shrink-0 w-7 h-[16px]"
                fill="currentColor"
                aria-hidden
              >
                <path
                  fillRule="nonzero"
                  d="M13.33 0L0 9.19 13.33 18.38V14c14.78 0 20.2 4.46 26.67 14-2.6-14.91-10.27-23.62-26.67-23.62V0z"
                />
              </svg>
              Exit Quiz
            </button>
          </div>

          <ExitModal isCompleted={isCompleted} />

          {isSurveyLoading ? (
            <div
              className="flex-1 flex flex-col items-center justify-center min-h-[280px] gap-4"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <span
                className="inline-block w-10 h-10 border-2 border-[#3b519c] border-t-transparent rounded-full animate-spin"
                aria-hidden
              />
              <p className="text-[15px] font-primary text-[#6b7280] m-0">
                Loading survey…
              </p>
              <span className="sr-only">Loading survey, please wait</span>
            </div>
          ) : (
            <>
              <div className="flex-shrink-0 mb-8">
                <span className="text-[14px] font-medium text-[#6b7280] font-primary">
                  {progress}%
                </span>
                <div className="mt-2 h-2 w-full rounded-sm bg-[#e5e7eb] overflow-hidden">
                  <div
                    className="h-full rounded-sm bg-[#3b519c] transition-[width] duration-300 ease-out"
                    style={{
                      width: `${Math.min(100, Math.max(0, progress))}%`,
                    }}
                  />
                </div>
              </div>

              <div className="flex-1 flex flex-col min-h-0">
                {currentQuestion && (
                  <QuestionRenderer
                    key={currentQuestion.key}
                    question={currentQuestion}
                    questionNumber={getCurrentQuestionOrdinal(
                      questions,
                      currentPage,
                    )}
                    answers={displayAnswers}
                    onAnswerChange={handleAnswerChangeAndAdvance}
                    onAnswersBatch={handleAnswersBatchAndMaybeAdvance}
                  />
                )}
              </div>

              <div className="flex-shrink-0 flex w-full flex-col-reverse items-stretch justify-center gap-3 pt-6 pb-4 sm:flex-row sm:items-center sm:gap-4">
                {currentPage > 0 && (
                  <button
                    type="button"
                    onClick={goPrevious}
                    className="btn btn-secondary btn-md w-full sm:w-auto"
                  >
                    Previous
                  </button>
                )}

                <button
                  type="button"
                  className="btn btn-primary btn-md inline-flex w-full items-center justify-center gap-2 sm:min-w-[160px] sm:w-auto"
                  onClick={() => void goNext()}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"
                        aria-hidden
                      />
                      Saving...
                    </>
                  ) : currentQuestion?.layout === "instructions" ? (
                    "Continue"
                  ) : (
                    "Save and Continue"
                  )}
                </button>
              </div>
            </>
          )}
        </Card>
      </PageContent>
    </HorizontalLayout>
  );
}
